/**
 * Dice ViewModel Hook
 *
 * Strict MVVM pattern: Contains ALL logic for Dice component.
 * The component itself should be completely logic-free, just rendering.
 */

import React from 'react';
import type { DiceProps } from '../constants/types';
import {
  calculateDotPositions,
  generateUniqueId,
  getRandomDiceFace,
  DICE_CONFIG
} from '../utils/utils';
import { buildClassNames } from '@/utils/classNames';
import {
  DICE_BOUNCE_ANIMATION,
  DICE_FACE_CYCLE_INTERVAL_MS,
  DICE_SETTLE_BEFORE_END_MS,
  createDiceRoll3DAnimation
} from '../../../animations/constants';
import type { ValueKeyframesDefinition } from 'framer-motion';

/**
 * ViewModel state returned to component
 */
interface DiceViewModelState {
  validValue: number;
  displayedValue: number;
  baseId: string;
  getId: (suffix: string) => string;
  dropShadowUrl: string;
  innerBevelUrl: string;
  dotGradientUrl: string;
  whiteVertUrl: string;
  whiteHorizUrl: string;
  dotHighlightRadius: number;
  dotHighlightOffset: number;
  dotPositions: { cx: number; cy: number }[];
  svgClassName: string;
  combinedAnimation: Record<string, ValueKeyframesDefinition>;
  canClick: boolean;
  cursorStyle: string;
}

/**
 * ViewModel actions returned to component
 */
interface DiceViewModelActions {
  handleClick: (event: React.MouseEvent<SVGSVGElement>) => void;
}

/**
 * Complete ViewModel interface
 */
interface DiceViewModel {
  state: DiceViewModelState;
  actions: DiceViewModelActions;
}

/**
 * Dice ViewModel
 * Processes all data and logic for the Dice component
 */
export function useDiceViewModel({
  value,
  className = '',
  isRolling = false,
  animationDuration = DICE_CONFIG.DEFAULT_ANIMATION_DURATION,
  onClick,
  disabled = false,
  isClickable = false,
  shouldBounce = false
}: DiceProps): DiceViewModel {
  // Validate value is 1-6, default to 0 (pending) if invalid
  const validValue = value >= 1 && value <= 6 ? value : 0;

  // State for the currently displayed face during rolling animation
  const [displayedValue, setDisplayedValue] = React.useState(validValue);

  // Ref to track latest validValue without triggering effect re-run.
  // This prevents the cycling animation from restarting when the server-authoritative
  // dice value arrives mid-animation (current user flow: isRolling set first, diceValue arrives later).
  const validValueRef = React.useRef(validValue);
  validValueRef.current = validValue;

  const cycleIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  // Face cycling effect for realistic rolling animation
  React.useEffect(() => {
    if (!isRolling) {
      if (cycleIntervalRef.current) {
        clearInterval(cycleIntervalRef.current);
        cycleIntervalRef.current = null;
      }
      setDisplayedValue(validValueRef.current || 1);
      return;
    }

    cycleIntervalRef.current = setInterval(() => {
      setDisplayedValue((prev) => getRandomDiceFace(prev));
    }, DICE_FACE_CYCLE_INTERVAL_MS);

    const settleDelay = Math.max(0, animationDuration - DICE_SETTLE_BEFORE_END_MS);
    const settleTimeout = setTimeout(() => {
      if (validValueRef.current >= 1) {
        // Server value available, settle normally
        if (cycleIntervalRef.current) {
          clearInterval(cycleIntervalRef.current);
          cycleIntervalRef.current = null;
        }
        setDisplayedValue(validValueRef.current);
      }
      // value is 0 (pending server response): keep cycling
    }, settleDelay);

    return () => {
      if (cycleIntervalRef.current) {
        clearInterval(cycleIntervalRef.current);
        cycleIntervalRef.current = null;
      }
      clearTimeout(settleTimeout);
    };
  }, [isRolling, animationDuration]);

  // Settle immediately when server value arrives during rolling (0→N)
  const prevValidValueRef = React.useRef(validValue);
  React.useEffect(() => {
    const wasZero = prevValidValueRef.current === 0;
    prevValidValueRef.current = validValue;

    if (isRolling && validValue >= 1 && wasZero) {
      if (cycleIntervalRef.current) {
        clearInterval(cycleIntervalRef.current);
        cycleIntervalRef.current = null;
      }
      setDisplayedValue(validValue);
    }
  }, [validValue, isRolling]);

  // Generate unique base ID for this dice instance to prevent conflicts
  const baseId = React.useMemo(() => generateUniqueId('dice'), []);
  const getId = (suffix: string) => `${baseId}_${suffix}`;

  // Memoize filter and gradient URLs to prevent recreation on every render
  const dropShadowUrl = React.useMemo(() => `url(#${getId('dropShadow')})`, [baseId]);
  const innerBevelUrl = React.useMemo(() => `url(#${getId('innerBevel')})`, [baseId]);
  const dotGradientUrl = React.useMemo(() => `url(#${getId('dotGradient')})`, [baseId]);
  const whiteVertUrl = React.useMemo(() => `url(#${getId('whiteVert')})`, [baseId]);
  const whiteHorizUrl = React.useMemo(() => `url(#${getId('whiteHoriz')})`, [baseId]);

  // Memoize dot highlight calculations to prevent repeated math operations
  const dotHighlightRadius = React.useMemo(
    () => DICE_CONFIG.DOT.RADIUS * DICE_CONFIG.DOT.HIGHLIGHT.SIZE_MULTIPLIER,
    []
  );
  const dotHighlightOffset = React.useMemo(
    () => DICE_CONFIG.DOT.RADIUS * DICE_CONFIG.DOT.HIGHLIGHT.OFFSET_MULTIPLIER,
    []
  );

  // Calculate dot positions for currently displayed value (cycles during rolling)
  const dotPositions = React.useMemo(() => calculateDotPositions(displayedValue), [displayedValue]);

  // Determine if dice can be clicked
  const canClick = isClickable && !disabled && !isRolling;

  // Handle click event
  const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
    if (canClick && onClick) {
      event.preventDefault();
      onClick();
    }
  };

  // Build className with interactive states
  const isDisabled: boolean = disabled ? true : isRolling;
  const svgClassName = buildClassNames(
    className,
    { condition: isClickable && canClick, className: 'dice-clickable' },
    {
      condition: isClickable && isDisabled,
      className: 'dice-disabled'
    }
  );

  // Merge animations based on state - use centralized animation constants
  const combinedAnimation = isRolling
    ? createDiceRoll3DAnimation(animationDuration)
    : shouldBounce
      ? DICE_BOUNCE_ANIMATION
      : {};

  return {
    state: {
      validValue,
      displayedValue,
      baseId,
      getId,
      dropShadowUrl,
      innerBevelUrl,
      dotGradientUrl,
      whiteVertUrl,
      whiteHorizUrl,
      dotHighlightRadius,
      dotHighlightOffset,
      dotPositions,
      svgClassName,
      combinedAnimation,
      canClick,
      cursorStyle: canClick ? 'pointer' : 'default'
    },
    actions: {
      handleClick
    }
  };
}
